import type { ElementType } from 'react';
import { useRef } from 'react';
import { clx } from '../lib/utils';

const defaultClassName = clx(
  '[&:not(:disabled)]:hover:brightness-60 inline-flex max-w-sm transition-all duration-200 disabled:opacity-50 [&:not(:disabled)]:active:scale-95'
);
const pillClassName = clx(
  defaultClassName,
  'bg-background-tertiary border-1 justify-center rounded-full border-black px-4 py-3'
);
const minimalSizeClassName = clx(
  'inline-flex min-h-12 min-w-12 items-center justify-center rounded-full bg-transparent p-1 [&:not(:disabled)]:active:scale-95'
);
const minimalSizeIconClassName = clx(
  minimalSizeClassName,
  '[&:not(:disabled)]:hover:bg-background-tertiary/50 [&:not(:disabled)]:active:bg-background-tertiary/50'
);

type ButtonVariant = 'default' | 'buttonPrimary' | 'underline' | 'minimalSize' | 'minimalSizeIcon';

const classNameMap: Record<ButtonVariant, string> = {
  default: defaultClassName,
  buttonPrimary: pillClassName,
  minimalSize: minimalSizeClassName,
  minimalSizeIcon: minimalSizeIconClassName,
  underline: clx(defaultClassName, 'font-semibold underline'),
};

interface IButtonProps<C extends ElementType = 'button'> {
  as?: C;
  className?: string;
  variant?: ButtonVariant;
}

type ButtonProps<C extends ElementType = 'button'> = IButtonProps<C> &
  Omit<React.ComponentPropsWithoutRef<C>, keyof IButtonProps<C>>;

export const Button = <C extends ElementType = 'button'>({
  as,
  className = '',
  variant = 'default',
  ...props
}: ButtonProps<C>) => {
  const buttonRef = useRef<React.ComponentPropsWithoutRef<C>>(null);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (props.onClick) {
      e.preventDefault();
      buttonRef.current?.classList.add('animate-press');
      setTimeout(() => {
        buttonRef.current?.classList.remove('animate-press');
        props.onClick?.(e);
      }, 200);
    }
  };

  const Comp = (as || 'button') as ElementType;
  return (
    <Comp
      className={clx(classNameMap[variant], className)}
      ref={buttonRef}
      {...props}
      onClick={handleClick}
    />
  );
};
